import { Box, Button, Skeleton } from '@mui/material';
import { useCallback, useState, useMemo, useEffect, memo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { format } from 'date-fns';
import { formatKeyForDisplay } from '@/utils';
import * as state from '@/components/Analysis/state';
import { PaintBlockGraph } from './Summary/PaintBlockGraph';
import { PaintBlockTable } from './PaintBlockTable';
import {
  useAllDecksQuery,
  useDefectLegendsForPaintBlockTableLazyQuery,
  useDefectsAreaAndPointOfInterestCountByPaintRegionsQuery,
  useDefectsAreaForCustomBlocksQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useGetStructureNameLazyQuery } from '@/__generated__/graphql';
import { XlsxProcessor } from '@/utils/xlsx';
import { PageLoader } from '@/components/shared/PageLoader';
import { containedButtonStyle } from '@/theme';
import { CORROSION_GROUPKEYS, PLATFORM_EXPORT_CORROSION_GROUPKEY } from '@/constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import {
  useActiveCoatingConditionLegends,
  useAllCoatingConditions,
  useTemplateFieldNames,
} from './Summary/hooks/useDefectsUtils';
import { PaintBlockTopFilter } from '@/components/Analysis/modules/CustomBlocks/components/PaintBlockTopFilter';
import { ErrorLogger } from '@/utils/error-logger';
import { INSIGHTS_DECK_WITH_PAINT_REGIONS_ERROR } from '@/constants/Errors';

export type DefectLegend = {
  id: number;
  name: string;
  groupKey: string;
  groupName: string;
  color?: string;
};

const PaintBlockContainerBase = () => {
  const [selectedPOIType, setSelectedPOIType] = useState<string[]>([]);
  const [selectedCoatingCondition, setSelectedCoatingCondition] = useState<string[]>([]);
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const [applyFilters, setApplyFilters] = useAtom(state.applyFilters);
  const structureId = useGetStructureId();
  const selectedTemplateFilters = useAtomValue(state.selectedTemplateFiltersState);
  const selectedPaintRegionGroupId = useAtomValue(state.selectedPaintRegionGroupId);
  const filteredDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const paintRegionIds = useAtomValue(state.selectedFilteredPaintRegionIds);
  const [insightsExplorerLoading, setInsightsExplorerLoading] = useAtom(
    state.insightsExplorerLoadingState
  );
  const showUncalibratePaintBlockdData = useAtomValue(state.showUncalibratePaintBlockdData);
  const { showSnackBar } = useSnackBarMessage({
    variant: 'filled',
  });
  const [selectedDecksIds, setSelectedDecksIds] = useState<string[]>([]);
  const [paintBlockTableXlsxProcessor, setPaintBlockTableXlsxProcessor] = useState<
    XlsxProcessor | undefined
  >(undefined);
  const [defectLegend, setDefectLegend] = useState<DefectLegend[] | undefined>([]);
  const [selectedConditionType, setSelectedConditionType] = useState<string>('');
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);

  const [fetchDefectLegends] = useDefectLegendsForPaintBlockTableLazyQuery();
  useEffect(() => {
    fetchDefectLegends({
      variables: {
        structureId,
      },
      fetchPolicy: 'cache-first',
    }).then(({ data: defectsData }) => {
      const defects = defectsData?.structure?.defectLegends;
      setDefectLegend(defects);
      const coatingCondition = formatKeyForDisplay(PLATFORM_EXPORT_CORROSION_GROUPKEY);
      const defaultConditionType =
        defects?.filter((defect) => defect.groupName === coatingCondition)[0]?.groupName ||
        defects?.[0]?.groupName;
      setSelectedConditionType(defaultConditionType || '');
    });
  }, [fetchDefectLegends, structureId]);

  useEffect(() => {
    setApplyFilters(true);
  }, [setApplyFilters]);

  const { data: defectAreaForPaintBlocksAndDecksResponse, loading: combinedLoading } =
    useDefectsAreaAndPointOfInterestCountByPaintRegionsQuery({
      variables: {
        input: {
          showUncalibratedData: showUncalibratePaintBlockdData,
          structureIds: [structureId],
          selectedTemplateFilters: appliedTemplateFilters,
          deckIds: [...new Set([...filteredDeckIds, ...selectedDecksIds])],
          paintRegionIds,
          paintRegionGroupIds: selectedPaintRegionGroupId
            ? [selectedPaintRegionGroupId]
            : undefined,
        },
      },
      skip: !selectedPaintRegionGroupId,
      nextFetchPolicy: 'cache-first',
      onCompleted: (data) => {
        setApplyFilters(false);

        // Handle Defects Area Data
        if (!data?.defectsAreaForAssemblyPaintRegions) {
          showSnackBar('Defects Area Data Not Found', 'error');
        }

        // Handle Point of Interest Data
        if (!data?.pointOfInterestCountByPaintRegions) {
          showSnackBar('Point of Interest Data Not Found', 'error');
        }
      },
    });

  const { data: defectAreaForCustomBlocksResponse } = useDefectsAreaForCustomBlocksQuery({
    variables: {
      input: {
        showUncalibratedData: showUncalibratePaintBlockdData,
        structureIds: [structureId],
        paintRegionGroupIds: selectedPaintRegionGroupId ? [selectedPaintRegionGroupId] : undefined,
        selectedTemplateFilters: appliedTemplateFilters,
        deckIds: [...new Set([...filteredDeckIds, ...selectedDecksIds])],
        paintRegionIds,
      },
    },
    fetchPolicy: 'cache-first',
  });

  const defectAreaForCustomBlocks = useMemo(
    () => defectAreaForCustomBlocksResponse?.defectsAreaForAssemblyPaintRegionsByGroup,
    [defectAreaForCustomBlocksResponse?.defectsAreaForAssemblyPaintRegionsByGroup]
  );

  const defectAreaForPaintBlocksAndDecks = useMemo(
    () => defectAreaForPaintBlocksAndDecksResponse?.defectsAreaForAssemblyPaintRegions,
    [defectAreaForPaintBlocksAndDecksResponse?.defectsAreaForAssemblyPaintRegions]
  );

  const poiCountForPaintBlocksAndDecks = useMemo(
    () => defectAreaForPaintBlocksAndDecksResponse?.pointOfInterestCountByPaintRegions,
    [defectAreaForPaintBlocksAndDecksResponse?.pointOfInterestCountByPaintRegions]
  );

  const {
    data: allDecksData,
    loading: allDecksDataLoading,
    error: allDecksDataError,
  } = useAllDecksQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  const [getStructureName] = useGetStructureNameLazyQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });
  const templateFieldNames = useTemplateFieldNames(defectLegend, selectedConditionType);
  const allCoatingConditions = useAllCoatingConditions(
    defectLegend,
    selectedConditionType,
    templateFieldNames,
    setSelectedCoatingCondition
  );

  const { allDecks } = useMemo(() => {
    if (allDecksDataError) {
      ErrorLogger.log(
        INSIGHTS_DECK_WITH_PAINT_REGIONS_ERROR.withContext({ structureId }).withError(
          allDecksDataError
        )
      );
    }
    return {
      allDecks: allDecksData?.allDecks ?? [],
    };
  }, [allDecksData?.allDecks, allDecksDataError, structureId]);

  const graphsLoading = useMemo(() => {
    return allDecksDataLoading || combinedLoading;
  }, [allDecksDataLoading, combinedLoading]);

  const changePOITypeTab = useCallback((key: string | null | undefined) => {
    if (!key) return;
    setSelectedPOIType((previousRows) =>
      previousRows.includes(key)
        ? previousRows.filter((rowName) => rowName !== key)
        : [...previousRows, key]
    );
  }, []);

  const changeCoatingTab = useCallback((name: string) => {
    setSelectedCoatingCondition((previousRows) =>
      previousRows.includes(name)
        ? previousRows.filter((rowName) => rowName !== name)
        : [...previousRows, name]
    );
  }, []);

  useEffect(() => {
    setSelectedDecksIds(allDecks.map(({ id }) => id));
  }, [allDecks]);

  useEffect(() => {
    if (applyFilters) {
      setAppliedTemplateFilters(selectedTemplateFilters);
    }
  }, [applyFilters, selectedTemplateFilters]);

  const allTableTemplates = useMemo(() => {
    const combinedData: Array<{ id: string; name: string }> = [];
    allPointOfInterestTemplates.forEach((item) => {
      if (item) combinedData.push({ id: item.id, name: item.name });
    });
    defectLegend?.forEach((item) => {
      combinedData.push({ id: item.id.toString(), name: item.name });
    });
    return combinedData;
  }, [allPointOfInterestTemplates, defectLegend]);

  const [structureName, setStructureName] = useState<string>('');

  useEffect(() => {
    getStructureName().then((result) => {
      setStructureName(result?.data?.structure?.name ?? 'platform');
    });
  }, [getStructureName]);

  const downloadXlsxFile = useCallback(async () => {
    if (paintBlockTableXlsxProcessor) {
      const structureNameQueryResult = await getStructureName();
      const currentStructureName = structureNameQueryResult?.data?.structure?.name ?? 'platform';
      const date = format(new Date(), 'dd-MMM-yyyy');
      const fileName = `${currentStructureName}-PaintBlock-${date}.xlsx`;
      paintBlockTableXlsxProcessor.download(fileName);
    }
  }, [getStructureName, paintBlockTableXlsxProcessor]);

  const activeCoatingConditionLegends = useActiveCoatingConditionLegends(
    defectLegend,
    templateFieldNames
  );

  useEffect(() => {
    if (!combinedLoading) {
      setInsightsExplorerLoading(false);
    }
  }, [combinedLoading, setInsightsExplorerLoading]);

  const defectsForTable = useMemo(() => {
    return defectLegend
      ?.filter((item) => CORROSION_GROUPKEYS.includes(item.groupKey))
      .map((item) => item.name.toLowerCase());
  }, [defectLegend]);

  const defaultPaintGroupCount = useMemo(() => {
    return defectAreaForCustomBlocks?.totalCount ?? 0;
  }, [defectAreaForCustomBlocks]);

  return (
    <>
      {insightsExplorerLoading ? (
        <Box sx={{ position: 'relative', top: '58px' }}>
          <PageLoader />
        </Box>
      ) : (
        <Box pb={4}>
          <Button
            id="export-paint-block-xlsx"
            data-testid="export-xlsx-btn"
            variant="contained"
            color="primary"
            size="small"
            sx={{ position: 'absolute', top: 8, right: 0, ...containedButtonStyle }}
            onClick={downloadXlsxFile}
          >
            Export Excel
          </Button>
          <PaintBlockTopFilter
            sx={{ mt: 3, gap: 1 }}
            defaultPaintGroupCount={defaultPaintGroupCount}
          />
          <PaintBlockGraph
            findingsByPaintBlocksAndDecks={poiCountForPaintBlocksAndDecks}
            defectAreaForPaintBlocksAndDecks={defectAreaForPaintBlocksAndDecks}
            defectAreaForCustomBlocks={defectAreaForCustomBlocks}
            selectedPOIType={selectedPOIType}
            changePOITypeTab={changePOITypeTab}
            changeCoatingTab={changeCoatingTab}
            allCoatingConditions={allCoatingConditions}
            selectedCoatingCondition={selectedCoatingCondition}
            selectedConditionType={selectedConditionType}
            setSelectedConditionType={setSelectedConditionType}
            graphsLoading={graphsLoading}
            coatingConditionLegend={activeCoatingConditionLegends}
            defects={defectLegend}
          />
          {combinedLoading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={80}
              sx={{ marginBottom: '10px' }}
            />
          ) : (
            defectAreaForPaintBlocksAndDecks && (
              <PaintBlockTable
                template={allTableTemplates}
                findingsByPaintBlock={poiCountForPaintBlocksAndDecks?.findingsByPaintBlock}
                defectsAreaByPaintBlocks={defectAreaForPaintBlocksAndDecks.defectsByPaintBlock}
                displayColumns={[...selectedPOIType, ...selectedCoatingCondition]}
                setPaintBlockTableXlsxProcessor={setPaintBlockTableXlsxProcessor}
                selectedConditionType={selectedConditionType}
                defects={defectsForTable}
                structureName={structureName}
              />
            )
          )}
        </Box>
      )}
    </>
  );
};

export const PaintBlockContainer = memo(PaintBlockContainerBase);
